import type { CompanyRegistration } from "@finn-no/mobility-dealer-types";
import pulse, { type TrackerData } from "@finn-no/pulse-sdk";
import { getCategoryForSearchKey } from "@m10s/mobility-mapping-utils";
// @ts-expect-error - The type is exported
import type { SearchKey } from "@m10s/search-frontend-clients/quest";
import { ElementType, EventType, Intent, TargetType } from "@mobility-item-page/utilities/client";
import { useMemo } from "react";
import { useOrganisation } from "../context/DealerDataProviderContext.tsx";

export const trackPageView = (pageTitle: string, orgId: number) => {
  pulse.trackPageView({
    type: EventType.View,
    object: {
      id: "DBP_pv01",
      type: "Page",
      name: pageTitle,
      pageType: "dealer_branding_page",
      contentId: orgId.toString(),
    },
    vertical: {
      name: "mobility",
    },
  });
};

type ClickData = Omit<TrackerData, "object"> & { object: NonNullable<TrackerData["object"]> };

export const usePulseTracking = () => {
  const { organisationId, name } = useOrganisation();

  return useMemo(() => {
    const trackClick = ({ object, ...data }: ClickData) =>
      pulse.trackEvent({
        ...data,
        type: EventType.Click,
        object: { ...object, contentId: organisationId.toString() },
        vertical: {
          name: "mobility",
        },
      });

    return {
      trackSeeLocation: (elementType: ElementType, linkUrl?: string) => {
        if (linkUrl) {
          const { hostname } = new URL(linkUrl);
          trackClick({
            intent: Intent.Open,
            name: "Dealer map",
            object: {
              id: "DBP_ev08",
              type: "UIElement",
              elementType,
            },
            target: {
              id: linkUrl,
              type: hostname === "google.com" || hostname.endsWith(".google.com") ? "ExternalContent" : "Page",
            },
          });
        }
      },
      trackSeeOtherLocation: (linkUrl?: string) => {
        if (linkUrl) {
          trackClick({
            intent: Intent.Open,
            name: "Other dealer map",
            object: {
              id: "DBP_ev13",
              type: "UIElement",
              elementType: ElementType.Link,
            },
            target: {
              id: linkUrl,
              type: "Page",
            },
          });
        }
      },
      trackBrandingPage: (linkUrl: string, providerName?: string) =>
        trackClick({
          intent: Intent.View,
          name: "Dealer branding page",
          object: { id: "DBP_ev11", type: "UIElement", elementType: ElementType.Link },
          target: {
            id: linkUrl,
            type: TargetType.Page,
            provider: providerName,
          },
        }),
      trackShowFullDescription: () =>
        trackClick({
          intent: Intent.View,
          name: "Full dealer description",
          object: {
            id: "DBP_ev05",
            type: "UIElement",
            elementType: ElementType.Button,
            hasExtendedProfile: false,
          },
        }),
      trackHomepage: (url?: string) =>
        trackClick({
          intent: Intent.Open,
          name: "Homepage link clicked",
          object: { id: "DBP_ev06", type: "UIElement", elementType: ElementType.Link },
          target: {
            id: url ?? "",
            type: TargetType.ExternalContent,
            name: "Go to webpage",
            provider: name,
          },
        }),
      trackCompanyRegistration: (companyRegistration: CompanyRegistration) =>
        trackClick({
          intent: Intent.Open,
          name: "Dealer registration number lookup",
          object: { id: "DBP_ev02", type: "UIElement", elementType: ElementType.Link },
          target: {
            id: companyRegistration.registrationNumberLinkUrl,
            type: TargetType.ExternalContent,
            name: "Go to webpage",
            provider: companyRegistration.registrationProviderName,
          },
        }),
      trackDealerRatings: (url: string) =>
        trackClick({
          intent: Intent.Open,
          name: "External dealer ratings",
          object: { id: "DBP_ev15", type: "UIElement", elementType: ElementType.Link },
          target: {
            id: url,
            type: TargetType.ExternalContent,
            name: "Go to webpage",
          },
        }),
      trackShowPhone: () =>
        trackClick({
          intent: Intent.Show,
          name: "Contact seller",
          object: {
            id: "DBP_ev03",
            type: "UIElement",
            elementType: "Phone number",
          },
          target: {
            id: "show-phone-numbers",
            type: TargetType.ExternalContent,
            name: "Phone",
            provider: name,
          },
        }),
      trackPhone: (intent: Intent.Call | Intent.Click, phoneNumber: string) =>
        trackClick({
          intent,
          name: "Contact seller",
          object: {
            id: "DBP_ev04",
            type: "UIElement",
            elementType: "Phone number",
          },
          target: {
            id: phoneNumber,
            type: TargetType.ExternalContent,
            name: "Phone",
            provider: name,
          },
        }),
      trackNavigationClick: (tab: string, linkUrl: string) =>
        trackClick({
          intent: Intent.View,
          name: "Selected page section",
          object: {
            id: "DBP_ev01",
            type: "UIElement",
            name: tab,
            elementType: ElementType.Link,
          },
          target: {
            id: linkUrl,
            type: "UIElement",
            name: `${tab} section`,
          },
        }),
      trackCarouselScroll: (direction: "backwards" | "forwards", searchKey: SearchKey) =>
        trackClick({
          intent: Intent.View,
          name: "Scroll more ads from us carousel",
          object: {
            id: "DBP_ev09",
            type: "UIElement",
            position: direction,
            elementType: ElementType.Button,
            category: getCategoryForSearchKey(searchKey, <URLSearchParams>{}),
          },
          target: {
            id: `${direction === "backwards" ? "previous" : "next"}-ads-in-carousel`,
            type: "UIElement",
          },
        }),
      trackSeeAllListings: (searchKey: string, link: string) =>
        trackClick({
          intent: Intent.View,
          name: "See all listings",
          object: {
            id: "DBP_ev10",
            type: "UIElement",
            elementType: ElementType.Button,
            category: getCategoryForSearchKey(searchKey, <URLSearchParams>{}),
          },
          target: {
            id: link,
            type: TargetType.Listing,
            provider: name,
          },
        }),
      trackSeeAdInCategory: (adId: number, adTitle?: string, searchKey?: string, linkUrl?: string) => {
        if (linkUrl) {
          trackClick({
            intent: Intent.Open,
            name: "Dealer ad",
            object: {
              id: "DBP_ev12",
              type: "ListingItem",
              name: adTitle,
            },
            target: {
              id: linkUrl,
              type: TargetType.ClassifiedAd,
              name: adTitle || "",
              contentId: adId.toString(),
              category: getCategoryForSearchKey(searchKey, <URLSearchParams>{}),
            },
          });
        }
      },
      trackViewMap: () =>
        trackClick({
          intent: Intent.Open,
          name: "View map",
          object: {
            id: "DBP_ev16",
            type: "UIElement",
            elementType: ElementType.Button,
            hasExtendedProfile: false,
          },
        }),
      trackOpenCookieSettings: () =>
        trackClick({
          intent: Intent.Edit,
          name: "Cookie settings",
          object: {
            id: "DBP_ev17",
            type: "UIElement",
            elementType: ElementType.Button,
            hasExtendedProfile: false,
          },
        }),
      trackClickMemberAssociationLink: ({
        targetTitle,
        linkUrl,
        targetType,
      }: {
        targetTitle: string;
        linkUrl: string;
        targetType: TargetType.Page | TargetType.ExternalContent;
      }) =>
        trackClick({
          intent: Intent.Open,
          name: "Read about member association",
          object: {
            id: "DBP_ev18",
            type: "UIElement",
            elementType: ElementType.Link,
          },
          target: {
            id: linkUrl,
            type: targetType,
            name: targetTitle,
          },
        }),
      trackOpenGallery: () =>
        trackClick({
          intent: Intent.Open,
          name: "Dealer Image Gallery",
          object: {
            id: "DBP_ev19",
            type: "UIElement",
            elementType: ElementType.Image,
            hasExtendedProfile: false,
          },
        }),
    };
  }, [organisationId, name]);
};
